/**
 * useMmsLayers Hook
 * 
 * Generates DeckGL layers for MMS (Model Management System) visualization.
 * Creates ScatterplotLayer for nodes and PathLayer for lines.
 */

import { useMemo, useCallback, useState, useRef } from 'react';
import { ScatterplotLayer, PathLayer } from 'deck.gl';
import type { MmsNode, MmsLine } from './useMmsMapData';

export interface UseMmsLayersProps {
	allNodes: MmsNode[];
	allLines: MmsLine[];
	nodeMap: Map<string, MmsNode>;
	viewState: {
		longitude: number;
		latitude: number;
		zoom: number;
		pitch?: number;
		bearing?: number;
	};
	enabled: boolean;
	mapRef?: { current: any };
	onNodeClick?: (node: MmsNode) => void;
	onLineClick?: (line: MmsLine) => void;
	onNodeDragStart?: (node: MmsNode) => void;
	onNodeDrag?: (node: MmsNode, lng: number, lat: number) => void;
	onNodeDragEnd?: (node: MmsNode, lng: number, lat: number) => void;
}

export interface UseMmsLayersResult {
	layers: any[];
	isDraggingNode: boolean;
	isDraggingNodeRef: React.MutableRefObject<boolean>;
	isHoveringNodeRef: React.MutableRefObject<boolean>;
	handleDragStart: (info: any) => void;
	handleDrag: (info: any) => void;
	handleDragEnd: (info: any) => void;
}

// Helper: Get infrastructure color based on type
const getInfrastructureColor = (infraType: string): string => {
	const type = infraType?.toLowerCase();

	// Node types - Purple
	if (['junctions', 'junction', 'outfalls', 'outfall', 'storage'].includes(type)) {
		return '#023e65';
	}

	// Line types - Blue
	if (['conduits', 'conduit', 'weirs', 'weir', 'pipes', 'pipe'].includes(type)) {
		return '#0077FF';
	}

	// Pumps - Orange
	if (['pumps', 'pump'].includes(type)) {
		return '#FF6B35';
	}

	// Default
	return '#0077FF';
};

// Helper: Get line color based on type and state
const getLinkColorFromType = (type: string, invisible?: boolean, edited?: boolean): string => {
	if (invisible) return '#f05775'; // Red for deleted
	if (edited) return '#79f865'; // Green for edited

	switch (type?.toUpperCase()) {
		case 'PIPE':
		case 'CONDUITS':
			return '#1e96be';
		case 'PUMPS':
			return '#ff991f';
		case 'ORIFICES':
		case 'WEIRS':
			return '#720c9d';
		default:
			return '#1e96be';
	}
};

// Helper: Convert hex color to RGB array
const hexToRgb = (hex: string): [number, number, number, number] => {
	const cleanHex = hex.replace('#', '');
	return [
		parseInt(cleanHex.substr(0, 2), 16),
		parseInt(cleanHex.substr(2, 2), 16),
		parseInt(cleanHex.substr(4, 2), 16),
		255,
	];
};

// Helper: Get node size based on zoom and dataset size
const getNodeSize = (zoom: number, totalNodes: number): number => {
	if (totalNodes >= 10000) {
		// Very large datasets: extra small nodes
		return zoom > 18 ? 3 : zoom > 16 ? 2.5 : zoom > 14 ? 2 : zoom > 12 ? 1.5 : 1;
	} else if (totalNodes >= 5000) {
		// Large datasets: small nodes
		return zoom > 18 ? 4 : zoom > 16 ? 3.5 : zoom > 14 ? 3 : zoom > 12 ? 2.5 : 1.5;
	} else {
		// Normal/small datasets: regular nodes
		return zoom >= 18.5 ? 9 : zoom > 18 ? 6 : zoom > 16 ? 4 : zoom > 14 ? 3 : zoom > 12.5 ? 2.5 : 2;
	}
};

// Helper: Get line width based on type and zoom
const getLineWidth = (type: string, zoom: number): number => {
	const baseWidth = type?.toLowerCase() === 'weirs' ? 3 : type?.toLowerCase() === 'pumps' ? 4 : 2;
	
	// Scale with zoom
	if (zoom > 16) return baseWidth * 1.5;
	if (zoom > 14) return baseWidth * 1.2;
	return baseWidth;
};

// Helper: Validate coordinates
const isValidCoordinate = (lng: number, lat: number): boolean => {
	return (
		typeof lng === 'number' &&
		typeof lat === 'number' &&
		lng >= -180 &&
		lng <= 180 &&
		lat >= -90 &&
		lat <= 90
	);
};

// Helper: Resolve map instance from mapRef
const getMapInstance = (mapRef?: { current: any }): any => {
	if (!mapRef?.current) return null;
	if (mapRef.current.getMap && typeof mapRef.current.getMap === 'function') {
		try { return mapRef.current.getMap(); } catch (e) { /* silent */ }
	}
	if (mapRef.current._map) {
		try { return mapRef.current._map; } catch (e) { /* silent */ }
	}
	if (mapRef.current.getBounds && typeof mapRef.current.getBounds === 'function') {
		try { return mapRef.current; } catch (e) { /* silent */ }
	}
	return null;
};

// Helper: Viewport culling using actual map bounds or fallback calculation
const getVisibleElements = (
	elements: MmsNode[],
	viewport: { longitude: number; latitude: number; zoom: number },
	padding = 0.05,
	mapRef?: { current: any },
): MmsNode[] => {
	const { longitude, latitude, zoom } = viewport;
	let bounds: { north: number; south: number; east: number; west: number } | null = null;

	const map = getMapInstance(mapRef);
	if (map && typeof map.getBounds === 'function') {
		try {
			const mapBounds = map.getBounds();
			const latRange = mapBounds.getNorth() - mapBounds.getSouth();
			const lngRange = mapBounds.getEast() - mapBounds.getWest();
			bounds = {
				north: mapBounds.getNorth() + latRange * padding,
				south: mapBounds.getSouth() - latRange * padding,
				east: mapBounds.getEast() + lngRange * padding,
				west: mapBounds.getWest() - lngRange * padding,
			};
		} catch (e) { /* silent */ }
	}

	if (!bounds) {
		const zoomFactor = Math.pow(2, zoom);
		const latRange = 360 / zoomFactor;
		const lngRange = 360 / zoomFactor;
		bounds = {
			north: latitude + latRange * (0.5 + padding),
			south: latitude - latRange * (0.5 + padding),
			east: longitude + lngRange * (0.5 + padding),
			west: longitude - lngRange * (0.5 + padding),
		};
	}

	return elements.filter((el) =>
		el.latitude != null && el.longitude != null &&
		el.latitude >= bounds!.south && el.latitude <= bounds!.north &&
		el.longitude >= bounds!.west && el.longitude <= bounds!.east,
	);
};

// Helper: Progressive node rendering based on zoom + dataset size
const getProgressiveNodes = (
	nodes: MmsNode[],
	zoom: number,
	viewport: { longitude: number; latitude: number; zoom: number },
	mapRef?: { current: any },
): MmsNode[] => {
	const totalNodes = nodes.length;
	let zoomThreshold: number;
	if (totalNodes < 5000) {
		zoomThreshold = 12.0;
	} else if (totalNodes < 10000) {
		zoomThreshold = 13.5;
	} else {
		zoomThreshold = 15.7;
	}

	if (zoom <= zoomThreshold) return [];

	if (totalNodes < 5000) return nodes;
	if (totalNodes < 10000) return getVisibleElements(nodes, viewport, 0.3, mapRef);

	let padding: number;
	if (zoom >= 17) padding = 0.5;
	else if (zoom >= 16) padding = 0.3;
	else if (zoom >= 15.5) padding = 0.2;
	else if (zoom >= 15) padding = 0.1;
	else padding = 0.05;
	return getVisibleElements(nodes, viewport, padding, mapRef);
};

// Helper: Check if a node is within viewport bounds
const isInViewport = (
	node: MmsNode,
	viewport: { longitude: number; latitude: number; zoom: number },
	padding = 0.5,
	mapRef?: { current: any },
): boolean => {
	const map = getMapInstance(mapRef);
	if (map && typeof map.getBounds === 'function') {
		try {
			const bounds = map.getBounds();
			const latRange = bounds.getNorth() - bounds.getSouth();
			const lngRange = bounds.getEast() - bounds.getWest();
			return (
				node.latitude >= bounds.getSouth() - latRange * padding &&
				node.latitude <= bounds.getNorth() + latRange * padding &&
				node.longitude >= bounds.getWest() - lngRange * padding &&
				node.longitude <= bounds.getEast() + lngRange * padding
			);
		} catch (e) { /* silent */ }
	}

	const { longitude, latitude, zoom } = viewport;
	const zoomFactor = Math.pow(2, zoom);
	const latRange = 360 / zoomFactor;
	const lngRange = 360 / zoomFactor;
	const fp = padding * 2;
	return (
		node.latitude >= latitude - latRange * fp &&
		node.latitude <= latitude + latRange * fp &&
		node.longitude >= longitude - lngRange * fp &&
		node.longitude <= longitude + lngRange * fp
	);
};

/**
 * Hook to generate MMS DeckGL layers
 */
export function useMmsLayers({
	allNodes,
	allLines,
	nodeMap,
	viewState,
	enabled,
	mapRef,
	onNodeClick,
	onLineClick,
	onNodeDragStart,
	onNodeDrag,
	onNodeDragEnd,
}: UseMmsLayersProps): UseMmsLayersResult {
	const [isDraggingNode, setIsDraggingNode] = useState(false);
	const isDraggingNodeRef = useRef(false); // Sync ref — updated before React batches state
	const isHoveringNodeRef = useRef(false); // True while cursor is over any node
	const draggedNodeRef = useRef<MmsNode | null>(null);
	// Local drag position — avoids writing to global allNodes state on every mousemove
	const [dragPosition, setDragPosition] = useState<{ nodeId: string; lng: number; lat: number } | null>(null);
	const dragPositionRef = useRef<{ nodeId: string; lng: number; lat: number } | null>(null);
	const { zoom } = viewState;

	// Drag handlers
	const handleDragStart = useCallback((info: any) => {
		if (!info.object || viewState.zoom < 13.6) return;
		isDraggingNodeRef.current = true; // Synchronous — blocks onViewStateChange immediately
		draggedNodeRef.current = info.object;
		setIsDraggingNode(true);
		if (onNodeDragStart) {
			onNodeDragStart(info.object);
		}
	}, [viewState.zoom, onNodeDragStart]);

	const handleDrag = useCallback((info: any) => {
		const node = draggedNodeRef.current;
		if (!isDraggingNodeRef.current || !node || !info.coordinate) return;
		const [lng, lat] = info.coordinate;
		if (!isValidCoordinate(lng, lat)) return;
		// Update local position only — avoids mapping over 10k+ allNodes on every mousemove
		const pos = { nodeId: node.id, lng, lat };
		dragPositionRef.current = pos;
		setDragPosition(pos);
	}, []);

	const handleDragEnd = useCallback((info: any) => {
		const node = draggedNodeRef.current;
		isDraggingNodeRef.current = false; // Synchronous reset
		draggedNodeRef.current = null;
		const finalPos = dragPositionRef.current;
		dragPositionRef.current = null;
		setDragPosition(null);
		if (!node) {
			setIsDraggingNode(false);
			return;
		}
		// Commit final position to global state exactly once (not 60fps)
		const lng = finalPos?.lng ?? info.coordinate?.[0];
		const lat = finalPos?.lat ?? info.coordinate?.[1];
		if (lng != null && lat != null && isValidCoordinate(lng, lat)) {
			if (onNodeDragEnd) onNodeDragEnd(node, lng, lat);
		}
		setIsDraggingNode(false);
	}, [onNodeDragEnd]);

	/**
	 * Progressive node visibility - matches original Mms/index.jsx visibleNodes logic
	 */
	const visibleNodes = useMemo(() => {
		if (!enabled || allNodes.length === 0) return [];

		const totalNodes = allNodes.length;
		let zoomThreshold: number;
		if (totalNodes < 5000) zoomThreshold = 12.0;
		else if (totalNodes < 10000) zoomThreshold = 13.5;
		else zoomThreshold = 15.7;

		// Below threshold: only show edited nodes that are in viewport
		if (zoom <= zoomThreshold) {
			return allNodes.filter(
				(node) => node.edited && isInViewport(node, viewState, 0.5, mapRef),
			);
		}

		// Above threshold: progressive rendering
		const progressiveNodes = getProgressiveNodes(allNodes, zoom, viewState, mapRef);

		// Always include edited nodes in viewport
		const editedNodes = allNodes.filter(
			(node) => node.edited && isInViewport(node, viewState, 0.5, mapRef),
		);

		// Include nodes connected to visible lines (avoid orphaned lines)
		const connectedNodeIds = new Set<string>();
		allLines.forEach((line) => {
			if (line.sourceNodeId) connectedNodeIds.add(line.sourceNodeId);
			if (line.targetNodeId) connectedNodeIds.add(line.targetNodeId);
		});
		const editedNodeIds = new Set(editedNodes.map((n) => n.id));
		const progressiveNodeIds = new Set(progressiveNodes.map((n) => n.id));
		const connectedNodes = allNodes.filter(
			(node) =>
				connectedNodeIds.has(node.id) &&
				!editedNodeIds.has(node.id) &&
				!progressiveNodeIds.has(node.id),
		);

		const regularNodes = progressiveNodes.filter((n) => !editedNodeIds.has(n.id));
		const finalConnected = connectedNodes.filter(
			(n) => !editedNodeIds.has(n.id) && !progressiveNodeIds.has(n.id),
		);
		const combined = [...editedNodes, ...regularNodes, ...finalConnected];

		// Viewport culling
		if (totalNodes < 5000) return combined;

		let viewportPadding: number;
		if (totalNodes >= 10000) {
			if (zoom >= 17) viewportPadding = 0.5;
			else if (zoom >= 16) viewportPadding = 0.3;
			else if (zoom >= 15.5) viewportPadding = 0.2;
			else if (zoom >= 15) viewportPadding = 0.1;
			else viewportPadding = 0.05;
		} else {
			viewportPadding = 0.8;
		}
		return combined.filter(
			(node) => isInViewport(node, viewState, viewportPadding, mapRef) || node.edited,
		);
	}, [enabled, allNodes, allLines, zoom, viewState, mapRef]);

	/**
	 * Nodes Layer - ScatterplotLayer
	 */
	const nodesLayer = useMemo(() => {
		if (!enabled || visibleNodes.length === 0) return null;

		const validNodes = visibleNodes.filter((node) =>
			isValidCoordinate(node.longitude, node.latitude)
		);

		if (validNodes.length === 0) return null;

		const nodeSize = getNodeSize(zoom, allNodes.length);

		return new ScatterplotLayer({
			id: 'mms-nodes-layer',
			data: validNodes,
			pickable: true,
			opacity: zoom > 10 ? 0.9 : 0.7,
			filled: true,
			radiusScale: 1,
			radiusMinPixels: nodeSize,
			radiusMaxPixels: nodeSize * 2,
			lineWidthMinPixels: 0.5,
			getPosition: (d: MmsNode) =>
			dragPosition && d.id === dragPosition.nodeId
				? [dragPosition.lng, dragPosition.lat]
				: [d.longitude, d.latitude],
			getRadius: nodeSize,
			getFillColor: (d: MmsNode) => {
				if (d.invisible) return [240, 87, 117, 220];   // Red — deleted
				if (d.edited)    return [52, 195, 143, 220];  // Green — edited/moved
				const color = getInfrastructureColor(d.type);
				return hexToRgb(color);
			},
			getLineColor: (d: MmsNode) => {
				if (d.invisible) return [200, 0, 0, 255];
				if (d.edited)    return [52, 195, 143, 220];
				return [255, 255, 255, 200];
			},
			onClick: (info: any) => {
				if (viewState.zoom >= 13.6 && info.object && onNodeClick && !isDraggingNode) {
					onNodeClick(info.object);
				}
			},
			onHover: (info: any) => {
				isHoveringNodeRef.current = !!info.object;
			},
			autoHighlight: true,
			highlightColor: [255, 255, 0, 100], // Yellow highlight on hover
			updateTriggers: {
				getPosition: [dragPosition],
				getFillColor: [allNodes],
				getLineColor: [allNodes],
				getRadius: [zoom, allNodes.length],
			},
		});
	}, [enabled, visibleNodes, allNodes, zoom, onNodeClick, isDraggingNode, dragPosition]);

	/**
	 * Lines Layer - PathLayer
	 */
	const linesLayer = useMemo(() => {
		if (!enabled || allLines.length === 0) return null;

		// Convert lines to path format using dynamic node positions
		const pathData = allLines
			.map((line) => {
				const sourceNode = nodeMap.get(line.sourceNodeId);
				const targetNode = nodeMap.get(line.targetNodeId);

				let path: number[][];

				if (sourceNode && targetNode) {
					// Preferred: use node positions from nodeMap (supports live drag)
					const srcLng = dragPosition && line.sourceNodeId === dragPosition.nodeId ? dragPosition.lng : sourceNode.longitude;
					const srcLat = dragPosition && line.sourceNodeId === dragPosition.nodeId ? dragPosition.lat : sourceNode.latitude;
					const tgtLng = dragPosition && line.targetNodeId === dragPosition.nodeId ? dragPosition.lng : targetNode.longitude;
					const tgtLat = dragPosition && line.targetNodeId === dragPosition.nodeId ? dragPosition.lat : targetNode.latitude;
					if (line.coordinates && line.coordinates.length > 2) {
						path = [
							[srcLng, srcLat],
							...line.coordinates.slice(1, -1).map((coord: [number, number]) => [coord[0], coord[1]]),
							[tgtLng, tgtLat],
						];
					} else {
						path = [[srcLng, srcLat], [tgtLng, tgtLat]];
					}
				} else if (line.coordinates && line.coordinates.length >= 2) {
					// Fallback: use the line's own coordinates (e.g. pumps whose endpoint
					// nodes may not be in nodeMap or are outside the current viewport)
					path = line.coordinates.map((coord) => [coord[0], coord[1]]);
				} else {
					return null;
				}

				const validPath = path.filter((coord) => isValidCoordinate(coord[0], coord[1]));
				if (validPath.length < 2) return null;

				return {
					...line,
					path: validPath,
					color: getLinkColorFromType(line.type, line.invisible, line.edited),
					width: getLineWidth(line.type, zoom),
					sourceNode,
					targetNode,
				};
			})
			.filter((line) => line !== null);

		if (pathData.length === 0) return null;

		return new PathLayer({
			id: 'mms-lines-layer',
			data: pathData,
			pickable: true,
			widthScale: 1,
			widthMinPixels: 1,
			widthMaxPixels: 10,
			getPath: (d: any) => d.path,
			getColor: (d: any) => hexToRgb(d.color),
			getWidth: (d: any) => d.width,
			onClick: (info: any) => {
				if (info.object && onLineClick) {
					onLineClick(info.object);
				}
			},
			autoHighlight: true,
			highlightColor: [255, 255, 0, 100],
			updateTriggers: {
				getColor: [allLines],
				getWidth: [zoom],
				getPath: [allLines, dragPosition],
			},
		});
	}, [enabled, allLines, nodeMap, zoom, onLineClick, dragPosition]);

	/**
	 * Combine all layers
	 */
	const layers = useMemo(() => {
		const result: any[] = [];
		if (linesLayer) result.push(linesLayer);
		if (nodesLayer) result.push(nodesLayer);
		return result;
	}, [nodesLayer, linesLayer]);

	return {
		layers: enabled ? layers : [],
		isDraggingNode,
		isDraggingNodeRef,
		isHoveringNodeRef,
		handleDragStart,
		handleDrag,
		handleDragEnd,
	};
}

export default useMmsLayers;
